/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: VoicemailsBroadcastGroupsFormView.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Form view for creating and editing Voicemail Broadcast Groups.
    Manages voicemail broadcast configuration with General and Settings sections.

 🔧 Notes:
    - Domain-scoped data
    - Auto-generates context from name
    - Two sections: General and Settings

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Voicemail, Phone, Settings, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import RecordingSelect from '../../components/ui/RecordingSelect';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FormBox } from '../../components/ui/FormBox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select-shadcn';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import {
  deleteVoicemailBroadcastGroupById,
  navigateToVoicemailBroadcastGroupDuplicate,
} from './voicemailsBroadcastGroupsActions';

interface NotificationSettings {
  method: 'none' | 'email' | 'api' | 'event';
  target: string;
}

interface FormSettings {
  announcement_path: string;
  distribution_mode: 'parallel' | 'sequential' | 'active_only';
  notification: NotificationSettings;
  max_record_time: number;
  max_retry: number;
  record_format: 'wav' | 'mp3' | 'ogg';
  delete_after_broadcast: boolean;
  announce_group_name: boolean;
  playback_volume: number;
  allowed_origins: string[];
}

interface FormState {
  code: string;
  name: string;
  context: string;
  password: string;
  skipInstructions: boolean;
  enabled: boolean;
  settings: FormSettings;
  members: string[];
}

const DEFAULT_SETTINGS: FormSettings = {
  announcement_path: '',
  distribution_mode: 'parallel',
  notification: {
    method: 'none',
    target: ''
  },
  max_record_time: 300,
  max_retry: 3,
  record_format: 'wav',
  delete_after_broadcast: false,
  announce_group_name: false,
  playback_volume: 5,
  allowed_origins: []
};

const DEFAULT_FORM_STATE: FormState = {
  code: '',
  name: '',
  context: '',
  password: '',
  skipInstructions: false,
  enabled: true,
  settings: { ...DEFAULT_SETTINGS, notification: { ...DEFAULT_SETTINGS.notification }, allowed_origins: [] },
  members: []
};

const CODE_REGEX = /^\*?\d+$/;

import { VoicemailBroadcastSettingsSection } from './components/VoicemailBroadcastSettingsSection';

export default function VoicemailsBroadcastGroupsFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotifications();

  const lastSaveRef = useRef<number>(0);
  const lastLoadedIdRef = useRef<string | null>(null);
  const showErrorRef = useRef(showError);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAllowedOriginsModal, setShowAllowedOriginsModal] = useState(false);

  const [formData, setFormData] = useState<FormState>({ ...DEFAULT_FORM_STATE });
  const [originalData, setOriginalData] = useState<FormState>({ ...DEFAULT_FORM_STATE });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableExtensions, setAvailableExtensions] = useState<Array<{ id: string; name: string; description: string; slug: string }>>([]);
  const extensionsLoadedRef = useRef(false);

  const hasChanges = useMemo(() => JSON.stringify(formData) !== JSON.stringify(originalData), [formData, originalData]);

  // Quick List (Voicemail Broadcast Groups) states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<{ id: string | number; code?: string; name?: string; [key: string]: unknown }[]>([]);
  const [initialItems, setInitialItems] = useState<{ id: string | number; code?: string; name?: string; [key: string]: unknown }[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isQuickSearching, setIsQuickSearching] = useState(false);

  const loadExtensions = useCallback(async () => {
    if (extensionsLoadedRef.current) return; // Already loaded or loading

    extensionsLoadedRef.current = true;
    try {
      const response = await fetch(
        buildApiUrl('/api/telephony/extensions?limit=1000&sortBy=extension&sortOrder=asc'),
        {
          headers: getApiHeaders()
        }
      );

      if (!response.ok) {
        throw new Error();
      }

      const result = await response.json();
      const items = Array.isArray(result?.data)
        ? result.data.map((ext: { extension: string | number; name?: string }) => ({
          id: String(ext.extension),
          name: `${ext.extension}${ext.name ? ` - ${ext.name}` : ''}`,
          description: ext.name || '',
          slug: String(ext.extension)
        }))
        : [];

      setAvailableExtensions(items);
    } catch (error) {
      extensionsLoadedRef.current = false; // Reset on error to allow retry
      showErrorRef.current(t('voicemailsBroadcastGroups:messages.loadExtensionsError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateContext = useCallback((name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 50);
  }, []);

  const normalizeSettings = useCallback((settings: Partial<FormSettings> & { notification?: Partial<NotificationSettings>; allowed_origins?: unknown[] } | null | undefined): FormSettings => {
    const normalized: FormSettings = {
      ...DEFAULT_SETTINGS,
      ...settings,
      notification: {
        ...DEFAULT_SETTINGS.notification,
        ...(settings?.notification || {})
      },
      allowed_origins: Array.isArray(settings?.allowed_origins) ? settings.allowed_origins : []
    };

    normalized.distribution_mode = ['parallel', 'sequential', 'active_only'].includes(normalized.distribution_mode)
      ? normalized.distribution_mode
      : 'parallel';
    normalized.notification.method = ['none', 'email', 'api', 'event'].includes(normalized.notification.method)
      ? normalized.notification.method
      : 'none';
    normalized.notification.target = (normalized.notification.target || '').trim();
    normalized.record_format = ['wav', 'mp3', 'ogg'].includes(normalized.record_format) ? normalized.record_format : 'wav';
    normalized.max_record_time = Math.min(3600, Math.max(10, Number(normalized.max_record_time) || 300));
    normalized.max_retry = Math.min(10, Math.max(0, Number(normalized.max_retry) || 3));
    normalized.playback_volume = Math.min(10, Math.max(1, Number(normalized.playback_volume) || 5));

    normalized.allowed_origins = Array.from(new Set(normalized.allowed_origins.map((ext) => String(ext))));

    return normalized;
  }, []);

  const loadVoicemailBroadcastGroup = useCallback(
    async (loadId: string, duplicate: boolean = false) => {
      setIsLoading(true);
      try {
        const response = await fetch(
          buildApiUrl(`/api/telephony/voicemails-broadcast-groups/${loadId}`),
          {
            headers: getApiHeaders()
          }
        );

        if (!response.ok) {
          throw new Error();
        }

        const result = await response.json();
        const group = result?.data;

        if (!group) {
          throw new Error();
        }

        const settings = normalizeSettings(group.settings);

        // Map members to extension strings (ensure they match the format used in availableExtensions)
        const members = group.members
          ? group.members.map((m: { extension?: string | number; id?: string | number }) => {
            // Try extension first, then id, then the member itself
            const extValue = m.extension || m.id || m;
            return String(extValue);
          })
          : [];

        const loaded: FormState = {
          code: group.code || '',
          name: group.name || '',
          context: group.context || '',
          password: group.password || '',
          skipInstructions: group.skip_instructions ?? false,
          enabled: group.enabled ?? true,
          settings,
          members
        };

        const copySuffix = t('common.copySuffix', 'Copy');
        const finalData = duplicate
          ? {
            ...loaded,
            code: '',
            name: loaded.name
              ? `${loaded.name} ${copySuffix}`.trim()
              : loaded.name
          }
          : loaded;

        setFormData(finalData);
        setOriginalData(finalData);
        setErrors({});
      } catch (error) {
        showErrorRef.current(t('voicemailsBroadcastGroups:messages.loadError'));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [normalizeSettings, t]
  );

  useEffect(() => {
    loadExtensions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTargetId = duplicateId || id;

    if (!loadTargetId || loadTargetId === 'new') {
      // Only reset if we haven't already reset for this 'new' state
      if (lastLoadedIdRef.current !== 'new') {
        lastLoadedIdRef.current = 'new';

        // Clear any saved data to ensure fresh form
        const savedDataKey = 'voicemailsBroadcastGroups:saveAndNewData';
        try {
          sessionStorage.removeItem(savedDataKey);
        } catch (error) {
          // Ignore errors when clearing
        }

        if (!duplicateId) {
          setFormData({ ...DEFAULT_FORM_STATE });
          setOriginalData({ ...DEFAULT_FORM_STATE });
        }
      }
      return;
    }

    if (lastLoadedIdRef.current === loadTargetId) {
      return;
    }

    lastLoadedIdRef.current = loadTargetId;

    loadVoicemailBroadcastGroup(loadTargetId, Boolean(duplicateId)).catch(() => {
      lastLoadedIdRef.current = null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateId, id]);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
    };
  }, []);

  const handleFieldChange = useCallback((field: keyof FormState, value: string | boolean) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value } as FormState;
      if (field === 'name' && typeof value === 'string' && value.trim()) {
        updated.context = generateContext(value);
      }
      return updated;
    });

    if (errors[field]) {
      setErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors[field];
        return updatedErrors;
      });
    }
  }, [errors, generateContext]);

  const handleSettingsChange = useCallback(<K extends keyof FormSettings>(key: K, value: FormSettings[K]) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value
      }
    }));
  }, []);

  const handleNotificationChange = useCallback(<K extends keyof NotificationSettings>(key: K, value: NotificationSettings[K]) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        notification: {
          ...prev.settings.notification,
          [key]: value
        }
      }
    }));

    if (key === 'target' && errors.notification_target) {
      setErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors.notification_target;
        return updatedErrors;
      });
    }
  }, [errors.notification_target]);

  const validateForm = useCallback(() => {
    const validationErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      validationErrors.code = t('voicemailsBroadcastGroups:validation.codeRequired');
    } else if (!CODE_REGEX.test(formData.code.trim())) {
      validationErrors.code = t('voicemailsBroadcastGroups:validation.codeInvalidFormat');
    }

    if (!formData.name.trim()) {
      validationErrors.name = t('voicemailsBroadcastGroups:validation.nameRequired');
    }

    if (formData.settings.max_record_time < 10 || formData.settings.max_record_time > 3600) {
      validationErrors.max_record_time = t('voicemailsBroadcastGroups:validation.maxRecordTimeRequired');
    }

    if (formData.settings.max_retry < 0 || formData.settings.max_retry > 10) {
      validationErrors.max_retry = t('voicemailsBroadcastGroups:validation.maxRetryRequired');
    }

    if (formData.settings.notification.method !== 'none' && !formData.settings.notification.target.trim()) {
      validationErrors.notification_target = t('voicemailsBroadcastGroups:validation.notificationTargetRequired');
    }

    setErrors(validationErrors);
    return { isValid: Object.keys(validationErrors).length === 0, errors: validationErrors };
  }, [formData, t]);

  const handleSubmit = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showErrorRef.current(`${t('voicemailsBroadcastGroups:messages.fixValidationErrors')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    const now = Date.now();
    if (now - lastSaveRef.current < 1000) {
      return undefined;
    }

    lastSaveRef.current = now;
    setIsSaving(true);

    try {
      const payload = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        description: formData.name.trim(),
        context: (formData.context || generateContext(formData.name)).trim(),
        password: formData.password || null,
        skipInstructions: formData.skipInstructions,
        enabled: formData.enabled,
        settings: formData.settings,
        members: formData.members || []
      };

      const url = isEditMode
        ? buildApiUrl(`/api/telephony/voicemails-broadcast-groups/${id}`)
        : buildApiUrl('/api/telephony/voicemails-broadcast-groups');
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(t('voicemailsBroadcastGroups:messages.saveError'));
      }

      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.message || t('voicemailsBroadcastGroups:messages.saveError'));
      }

      showSuccess(
        isEditMode
          ? t('voicemailsBroadcastGroups:messages.saveSuccessUpdate')
          : t('voicemailsBroadcastGroups:messages.saveSuccessCreate')
      );

      // If editing and we have updated data from backend, use it to ensure sync
      if (isEditMode && result.data) {
        const updatedGroup = result.data;
        const settings = normalizeSettings(updatedGroup.settings);
        const members = updatedGroup.members
          ? updatedGroup.members.map((m: { extension?: string | number; id?: string | number }) => String(m.extension || m.id || m))
          : [];

        const nextOriginal: FormState = {
          code: updatedGroup.code || formData.code,
          name: updatedGroup.name || formData.name,
          context: updatedGroup.context || formData.context,
          password: updatedGroup.password || formData.password,
          skipInstructions: updatedGroup.skip_instructions ?? formData.skipInstructions,
          enabled: updatedGroup.enabled ?? formData.enabled,
          settings,
          members
        };

        setOriginalData(nextOriginal);
        setFormData(nextOriginal);
      } else {
        // For create mode, just update with current form data
        const nextOriginal = {
          ...formData,
          context: payload.context
        };
        setOriginalData(nextOriginal);
        setFormData(nextOriginal);
      }

      // Return saved data (navigation is now handled by useSaveWithAction)
      return result.data || { ...payload, id: result.data?.id };
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('voicemailsBroadcastGroups:messages.saveError');
      showErrorRef.current(msg);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formData, generateContext, hasChanges, id, isEditMode, showInfo, showSuccess, t, validateForm]);

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      return { id, ...formData };
    }

    return savedData;
  }, [handleSubmit, formData, isEditMode, id]);

  // Use reusable hook for save actions
  const modeForSave = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/voicemail-broadcast',
    newPath: '/pbx/applications/voicemail-broadcast/new',
    mode: modeForSave,
    getEditPath: (savedData) => `/pbx/applications/voicemail-broadcast/${savedData.id || savedData.uuid}`,
    getFormData: () => formData,
    storageKey: 'voicemailsBroadcastGroups:saveAndNewData',
  });

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormData(originalData);
      setErrors({});
    } else {
      setFormData({ ...DEFAULT_FORM_STATE });
      setErrors({});
    }
  }, [isEditMode, originalData]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteVoicemailBroadcastGroupById(id);

      showSuccess(t('voicemailsBroadcastGroups:messages.deleteSuccess'));
      navigate('/pbx/applications/voicemail-broadcast');
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('voicemailsBroadcastGroups:messages.deleteError');
      showErrorRef.current(msg);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showSuccess, t]);

  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToVoicemailBroadcastGroupDuplicate(navigate, id);
  };

  // Navigate to new
  const handleAddNew = () => {
    navigate('/pbx/applications/voicemail-broadcast/new');
  };

  // Quick List loader for Voicemail Broadcast Groups
  // Load initial items when Quick List modal opens
  const loadInitialItems = useCallback(async () => {
    setIsLoadingInitial(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'code',
        sortOrder: 'asc'
      });

      const response = await fetch(
        buildApiUrl(`/api/telephony/voicemails-broadcast-groups?${params.toString()}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) {
          setInitialItems(result.data);
          setQuickListItems(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading initial items:', error);
    } finally {
      setIsLoadingInitial(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuickList = useCallback(async (search: string) => {
    const normalized = search.trim();

    // If search is empty or less than 2 characters, restore initial items
    if (!normalized || normalized.length < 2) {
      setQuickListItems((prev) => {
        // Use functional update to avoid dependency on initialItems
        return prev;
      });
      return;
    }

    setIsQuickSearching(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        sortBy: 'code',
        sortOrder: 'asc',
        search: normalized
      });

      const response = await fetch(
        buildApiUrl(`/api/telephony/voicemails-broadcast-groups?${params.toString()}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) {
          setQuickListItems(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading quick list items:', error);
    } finally {
      setIsQuickSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load initial items when Quick List modal opens
  useEffect(() => {
    if (showQuickList && initialItems.length === 0) {
      loadInitialItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQuickList, initialItems.length]);

  // Debounced Quick List search
  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        // Restore initial items if search is cleared
        setQuickListItems((prev) => prev);
      }
    }, 800);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickListSearch, showQuickList]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
      icon={<Voicemail className="h-5 w-5 text-primary" />}
      title={isEditMode ? `${t('voicemailsBroadcastGroups:editTitle')} - ${formData.name || ''}` : t('voicemailsBroadcastGroups:createTitle')}
      backButtonText={t('voicemailsBroadcastGroups:actions.back')}
      onBack={() => navigate('/pbx/applications/voicemail-broadcast')}
      actions={[
        // 1. Quick Search (always visible)
        {
          icon: <Search className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('voicemailsBroadcastGroups:actions.quickList'),
          onClick: () => setShowQuickList(true)
        },
        // 2. Duplicate (edit mode only)
        ...(isEditMode
          ? [{
            icon: <Copy className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('voicemailsBroadcastGroups:actions.duplicate'),
            onClick: handleDuplicateFromForm
          }]
          : []),
        // 3. Add New (always visible)
        {
          icon: <Plus className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('voicemailsBroadcastGroups:actions.add'),
          onClick: handleAddNew,
        }
      ]}
    />

      <div className="mt-8">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
          className="space-y-6"
        >
          <FormBox title={t('voicemailsBroadcastGroups:sections.general')} icon={<Voicemail className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              <Label htmlFor="code">
                <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.code.tooltip')}>
                  <span>
                    {t('voicemailsBroadcastGroups:fields.code.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(event) => handleFieldChange('code', event.target.value)}
                  placeholder={t('voicemailsBroadcastGroups:fields.code.placeholder')}
                  className={errors.code ? 'border-red-500' : ''}
                />
                {errors.code && <p className="text-sm text-red-500 mt-1">{errors.code}</p>}
              </div>

              <Label htmlFor="name">
                <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.name.tooltip')}>
                  <span>
                    {t('voicemailsBroadcastGroups:fields.name.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(event) => handleFieldChange('name', event.target.value)}
                  placeholder={t('voicemailsBroadcastGroups:fields.name.placeholder')}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
              </div>

              <Label htmlFor="password">
                <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.password.tooltip')}>
                  {t('voicemailsBroadcastGroups:fields.password.label')}
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(event) => handleFieldChange('password', event.target.value)}
                  placeholder={t('voicemailsBroadcastGroups:fields.password.placeholder')}
                />
              </div>

              <Label>
                <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.skipInstructions.tooltip')}>
                  {t('voicemailsBroadcastGroups:fields.skipInstructions.label')}
                </SimpleTooltip>
              </Label>
              <div>
                <Toggle value={formData.skipInstructions} onChange={(value) => handleFieldChange('skipInstructions', value)} />
              </div>

              <Label>
                <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.enabled.tooltip')}>
                  {t('voicemailsBroadcastGroups:fields.enabled.label')}
                </SimpleTooltip>
              </Label>
              <div>
                <Toggle value={formData.enabled} onChange={(value) => handleFieldChange('enabled', value)} />
              </div>
            </div>
          </FormBox>

          <VoicemailBroadcastSettingsSection
            settings={formData.settings}
            members={formData.members}
            errors={errors}
            availableExtensions={availableExtensions}
            showMembersModal={showAllowedOriginsModal}
            setShowMembersModal={setShowAllowedOriginsModal}
            onSettingsChange={handleSettingsChange}
            onNotificationChange={handleNotificationChange}
            onMembersChange={(selected) => setFormData((prev) => ({ ...prev, members: selected }))}
            t={t}
          />
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        deleteDisabled={isDeleting}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="voicemailsBroadcastGroups:saveAction"
        saveDisabled={isSaving}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('voicemailsBroadcastGroups:messages.deleteTitle')}
        message={t('voicemailsBroadcastGroups:messages.confirmDeleteGeneric')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Quick List Modal for Voicemail Broadcast Groups */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('voicemailsBroadcastGroups:title')}
        items={quickListItems}
        isLoadingInitial={isLoadingInitial}
        isSearching={isQuickSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/voicemail-broadcast/${item.uuid || item.id}`);
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/voicemail-broadcast/${item.uuid || item.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <Voicemail className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-foreground">
                    {item.code}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('voicemailsBroadcastGroups.current', 'Current')}
                    </span>
                  )}
                </div>
                {item.name && (
                  <div className="text-sm text-muted-foreground truncate">
                    {item.name}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('common.searchPlaceholder')}
        minCharsMessage={t('voicemailsBroadcastGroups.quickListMinChars', 'Type at least 2 characters to search')}
        emptyMessage={t('voicemailsBroadcastGroups.quickListEmpty', 'Start typing to search voicemail broadcast groups')}
        noResultsMessage={t('voicemailsBroadcastGroups.quickListNoResults', 'No voicemail broadcast groups found')}
        headerIcon={<Voicemail className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}

